import type { QueryClient } from '@tanstack/react-query'

import api from './api'
import type { DiscoveredDatapoint } from './discoveredDatapoints'
import type {
  DocRef,
  Provenance,
  ReconcileNeedsPerson,
  ReparseProposal,
  ReviewItem,
  SourceFileRef,
} from './ingestion'
import { invalidateLoanQueries } from './loanQueryKeys'
import { toCamelCase, toSnakeCase } from '@/utils/transform'

/**
 * Data Intelligence Review — every question the platform owes a person, read
 * from one place and answered through one route.
 *
 * Three screens used to ask the same job of a person: the per-record checklist,
 * the engine's re-parse proposals and the labels no field holds yet. The server
 * now joins them into one row shape, ranked; this module only types it and
 * carries a decision back. It never ranks, never decides which row to offer
 * and has no verb that answers more than one row.
 */

export type DataReviewSource = 'review' | 'reparse' | 'reconcile' | 'discovery'

/** What a row offers. `point` is a way of correcting: show the engine the place. */
export type DataReviewAction =
  | 'confirm' | 'correct' | 'point' | 'unavailable' | 'accept' | 'dismiss' | 'withdraw'

/** What can be sent. `point` is not one — it sends `correct` with `located`. */
export type DataReviewVerb =
  | 'confirm' | 'correct' | 'unavailable' | 'withdraw' | 'accept' | 'dismiss'

/** A value a person may choose, with where it came from. */
export interface DataReviewOption {
  value: unknown
  source: string | null
  relation: string | null
  origin: 'candidate' | 'engine_reading' | 'derived' | 'located'
  provenance: Provenance | null
  /** What kind of reading this is (period and tab), for a workbook figure. */
  basis?: string | null
}

/** What a question that is not answered here is, and where it is answered. */
export interface DataReviewElsewhere {
  where: string
  detail: string
  workbook?: string | null
}

/** A sweep finding that needs a person. The two counterparty kinds also carry
 *  the rival names, read live. */
export interface DataReviewNeedsPerson extends ReconcileNeedsPerson {
  candidates?: { value: unknown; source: string | null }[]
}

/** The source structure, verbatim — so the cards the old screens drew render
 *  unchanged. Absent on the inbox, which is deliberately lean. */
export interface DataReviewDetail {
  reviewItem?: ReviewItem
  proposal?: ReparseProposal
  needsPerson?: DataReviewNeedsPerson
  /** When the sweep that found a `needsPerson` question ran. */
  asOf?: string | null
  discovered?: DiscoveredDatapoint
}

export interface DataReviewItem {
  /** `review:{record}:{field}` · `reparse:{record}:{field}` ·
   *  `reconcile:{record}:{field|-}:{kind}` · `discovery:{id}` */
  id: string
  source: DataReviewSource
  ingestionId: string | null
  loanId: string | null
  field: string | null
  label: string
  /** The checklist status, the re-parse kind, the sweep's kind, or whether a
   *  found datapoint is a new field or only a new word. `decided` is a person's
   *  answer kept reachable so it can be withdrawn. */
  kind: string
  /** Why this needs a person, in a sentence. */
  question: string
  value: unknown
  valueSource: string | null
  options?: DataReviewOption[]
  humanDecided: boolean
  decidedBy: string | null
  /** The pipeline grid draws a marker for it when it is blank or unsure. */
  pipelineCritical: boolean
  actions: DataReviewAction[]
  actionElsewhere: DataReviewElsewhere | null
  rank: number
  /** What kind of work the row is. Only `decision` is owed by a person; the rest
   *  are listed with their evidence and actions but do not count as owed. Absent
   *  on a found datapoint. */
  bucket?: DataReviewBucket
  /** The bucket in a sentence. */
  bucketNote?: string | null
  detail?: DataReviewDetail
}

/** `decision` is a question only a person can settle. `gap`: no document states
 *  it and the desk does not act on it. `engine_reread`: the nightly sweep applies
 *  the engine's new reading. `informational`: a person already decided it.
 *  `answered`: a person decided it after the sweep wrote its note. `duplicate`:
 *  the same question as an open checklist row. `elsewhere`: a task for another
 *  screen. `supporting_mail`: a checklist row on a reply or re-send filed on a
 *  deal — it describes that message; the deal's questions are asked on the
 *  record the deal counts. `decided`: a person's answer, kept reachable so it can
 *  be withdrawn. */
export type DataReviewBucket =
  | 'decision' | 'gap' | 'engine_reread' | 'informational' | 'answered'
  | 'duplicate' | 'elsewhere' | 'supporting_mail' | 'model_detail' | 'decided'

/**
 * Open rows that are not a decision owed, by what they are. The server keys this
 * map by bucket name, and `toCamelCase` renames KEYS on the way in, so the
 * server's `engine_reread` arrives here as `engineReread`. A row's own `bucket`
 * is a value, not a key, and keeps the server's spelling (`'engine_reread'`).
 * Read under the server's spelling, every engine re-read went unnamed on screen.
 */
export interface DataReviewSettled {
  gap: number
  engineReread: number
  informational: number
  answered: number
  duplicate: number
  elsewhere: number
  /** `supporting_mail` on the wire. Absent from an older server. */
  supportingMail?: number
  /** `model_detail` on the wire: lines the sponsor's model states several ways, on
   *  fields nothing in processing reads. Listed with every reading; not owed. */
  modelDetail?: number
}

/** One intake record with open questions, as the inbox lists it. */
export interface DataReviewDeal {
  ingestionId: string
  loanId: string | null
  subject: string | null
  /** `property_name` on the wire; `toCamelCase` maps it to `property`. */
  property: string | null
  status: string
  partition: string
  receivedAt: string | null
  /** The three public lookup fields the source viewer needs, never a path. */
  rawFilePaths: SourceFileRef[]
  /** The decisions a person owes on this record. */
  open: number
  pipelineCritical: number
  humanDecided: number
  /** Its other open rows, by what they are: listed, never counted as owed. */
  settled?: DataReviewSettled
  counts: { review: number; reparse: number; reconcile: number }
  items: DataReviewItem[]
}

/** The same record with the evidence behind its rows. */
export interface DataReviewRecord extends DataReviewDeal {
  reviewMeta: {
    counts: Record<string, number>
    open: number
    needsReview: boolean
    documents: DocRef[]
  }
  /** Other records of the same deal group. */
  siblings: { ingestionId: string; subject: string | null; status: string; receivedAt: string | null }[]
}

export interface DataReviewDiscovery {
  /** False when the account does not hold the Pipeline module the found
   *  datapoints belong to — and `reason` says so, rather than an empty list. */
  available: boolean
  reason: string | null
  items: DataReviewItem[]
  bands: { auto: number; review: number; weak: number }
  thresholds: { auto: number; weak: number }
  /** How many are waiting — not how many are listed. */
  total: number
  /** The list is the strongest of them: `total` is more than `items` holds. */
  truncated?: boolean
  /** Of the backed ones, how many a person may add now (two live deals in two
   *  files, evidence not weak) and how many are still being watched for a second
   *  deal. Absent on an older server. */
  addable?: number
  watching?: number
}

export interface DataReviewRun {
  id: string
  createdAt: string | null
  status: string
  triggeredBy: string | null
  needsPerson: number | null
}

export interface DataReviewTotals {
  /** Records with an open row of any kind. */
  deals: number
  /** Of those, the records a person owes a decision on. Absent on an older server. */
  recordsOwed?: number
  /** The decisions a person owes across the desk. */
  open: number
  pipelineCritical: number
  humanDecided: number
  bySource: { review: number; reparse: number; reconcile: number }
  /** Found datapoints at least one live deal states. */
  discovery: number
  /** Found datapoints no live deal states any more — every reading came from a
   *  record that no longer exists. Left off the list unless asked for, and
   *  absent from an older server, which does not tell the two apart. */
  discoveryUnbacked?: number
  /** Of the found datapoints, the ones a person may add now, and the ones the
   *  platform will not let anyone add yet. Absent on an older server. */
  discoveryAddable?: number
  discoveryWatching?: number
  /** Open rows that are not a decision owed, by what they are, summed over the
   *  desk. Absent on an older server. */
  settled?: Partial<DataReviewSettled>
  /** What the Intake tab badges: the decisions a person owes on deal data. Found
   *  datapoints are a vocabulary list, counted beside it and never in it. */
  all: number
}

/** Which records of the desk this answer holds. The totals beside it are the
 *  desk's, never the page's. */
export interface DataReviewPage {
  offset: number
  limit: number
  returned: number
  /** Records on the desk with a question open — not how many came back. */
  total: number
  hasMore: boolean
}

export interface DataReviewInbox {
  deals: DataReviewDeal[]
  discovery: DataReviewDiscovery
  totals: DataReviewTotals
  page: DataReviewPage
  latestRun: DataReviewRun | null
}

/** One record, in full. */
export interface DataReviewRecordView extends DataReviewRecord {
  discovery: DataReviewDiscovery
  latestRun: DataReviewRun | null
}

/** One deal, across every record it was promoted from. */
export interface DataReviewByLoan {
  loanId: string
  property: string | null
  deals: DataReviewRecord[]
  discovery: DataReviewDiscovery
  latestRun: DataReviewRun | null
}

export interface DataReviewDecisionInput {
  itemId: string
  verb: DataReviewVerb
  /** The value a person typed or picked. Required for `correct`. */
  value?: unknown
  /** Where the value was read: carried onto the decision as its evidence. */
  provenance?: Provenance | null
  /** True only for "point at it" — the engine read the place the person showed it. */
  located?: boolean
  /** Required for `unavailable`. */
  reason?: string
  note?: string
  /** Explicitly overrule a person's earlier decision on this row. Per call,
   *  never remembered, and recorded against the caller's name. */
  overrideHumanDecision?: boolean
  /** What a person corrected about a found datapoint on the way through. */
  discovery?: { name?: string; label?: string; kind?: string; group?: string; unit?: string | null }
}

/**
 * Which found datapoints a bulk dismissal covers. The criteria narrow one
 * another; the server answers only for proposed rows of the live desk.
 */
export interface DiscoveryDismissInput {
  /** Found datapoint ids — the `dd-…` id, not the review row's `discovery:` id. */
  ids?: string[]
  /** Every found datapoint read from this file. */
  document?: string
  /** General-ledger account lines ("7010.000 - maint equipment"). */
  accountCodes?: boolean
  /** The ones no live deal states any more. */
  unbacked?: boolean
  /** Count what would be dismissed and write nothing. True unless set false. */
  preview?: boolean
  /** Recorded with each dismissal. */
  note?: string
}

export interface DiscoveryDismissResult {
  preview: boolean
  count: number
  ids: string[]
  /** The first 50 labels, so a confirmation can say what it covers. */
  labels: string[]
}

export interface DataReviewMirror {
  mirrored: boolean
  why?: string | null
  [key: string]: unknown
}

export interface DataReviewDecisionResult {
  itemId: string
  outcome: string
  applied: Record<string, unknown>
  /** What reached the deal, or why nothing did. Null when no deal is linked. */
  mirroredToLoan: DataReviewMirror | null
  /** The record as it stands now; null for a found datapoint. */
  record: DataReviewRecordView | null
}

/** A refusal the screen can act on. */
export interface DataReviewRefusal {
  status: number | null
  /** `human_decided` · `already_settled` · `deal_value_set_by_person` · `demo` … */
  code: string | null
  message: string
  decidedBy?: string | null
  decidedAt?: string | null
  /** A checklist decision exists that can be withdrawn instead of overruled. */
  withdrawable?: boolean
  /** The refusal can be lifted by asking again with an explicit overrule: a
   *  person's decision, or a value a person set on the deal itself. */
  overridable?: boolean
  /** The record as it stands now, sent with an `already_settled` refusal. */
  record?: DataReviewRecordView | null
}

/** How many records the inbox asks for first, and for each "show more". The
 *  server's own default, so the first answer needs no parameters. */
export const INBOX_PAGE = 200

/** How long an inbox read stays fresh. The Intake tab badges from the same read
 *  its inbox lists, and used to fetch the desk again every time it was opened;
 *  a decision still refreshes it at once (`invalidateAfterDecision`). */
export const INBOX_STALE_MS = 60_000

export const dataReviewKeys = {
  all: ['data-review'] as const,
  /** The first page — what the Intake badge and the inbox both read. */
  inbox: ['data-review', 'inbox'] as const,
  /** The inbox asked for `limit` records: the first page's key until it grows.
   *  The answer that also lists the unbacked found datapoints has a key of its
   *  own: it holds rows the first page does not. */
  inboxFor: (limit: number = INBOX_PAGE, includeUnbacked = false) =>
    (includeUnbacked
      ? ['data-review', 'inbox', limit, 'unbacked']
      : limit === INBOX_PAGE ? ['data-review', 'inbox'] : ['data-review', 'inbox', limit]) as readonly unknown[],
  record: (ingestionId: string) => ['data-review', 'record', ingestionId] as const,
  loan: (loanId: string) => ['data-review', 'loan', loanId] as const,
}

export const dataReview = {
  /** Every open question on the desk, lean: no evidence blocks, no options. The
   *  records come a page at a time, worst first; the totals are the whole desk's.
   *  `includeUnbacked` also lists the found datapoints no live deal states. */
  inbox: (options: { limit?: number; offset?: number; includeUnbacked?: boolean } = {}) =>
    api
      .get('/ingestion/data-review', {
        params: {
          limit: options.limit,
          offset: options.offset,
          ...(options.includeUnbacked ? { include_unbacked: true } : {}),
        },
      })
      .then((r) => toCamelCase<DataReviewInbox>(r.data)),

  /** One record's questions with the evidence the panel renders. */
  record: (ingestionId: string) =>
    api
      .get(`/ingestion/data-review/${encodeURIComponent(ingestionId)}`)
      .then((r) => toCamelCase<DataReviewRecordView>(r.data)),

  /** Everything open on one deal, across every record it was promoted from. */
  byLoan: (loanId: string) =>
    api
      .get(`/ingestion/data-review/by-loan/${encodeURIComponent(loanId)}`)
      .then((r) => toCamelCase<DataReviewByLoan>(r.data)),

  /** One verb on one row. */
  decide: (input: DataReviewDecisionInput) =>
    api
      .post('/ingestion/data-review/decide', {
        item_id: input.itemId,
        verb: input.verb,
        value: input.value,
        // The block arrived camel-cased; the engine reads `line_no`, not `lineNo`.
        provenance: input.provenance ? toSnakeCase(input.provenance) : undefined,
        located: input.located,
        reason: input.reason,
        note: input.note,
        override_human_decision: input.overrideHumanDecision,
        discovery: input.discovery,
      })
      .then((r) => toCamelCase<DataReviewDecisionResult>(r.data)),

  /** "Not a datapoint", for many found datapoints at once — dismissal only,
   *  never an add. A preview unless `preview` is false; applied, each row is
   *  dismissed and audited exactly as the single decision would. */
  dismissDiscovery: (input: DiscoveryDismissInput) =>
    api
      .post('/ingestion/data-review/discovery/dismiss', {
        ids: input.ids,
        document: input.document,
        accountCodes: input.accountCodes,
        unbacked: input.unbacked,
        preview: input.preview ?? true,
        note: input.note,
      })
      .then((r) => toCamelCase<DiscoveryDismissResult>(r.data)),
}

/** What a failed call said, as something the screen can act on. An error body
 *  is not run through `toCamelCase` by the client, so it is read as sent. */
export function refusalOf(error: unknown): DataReviewRefusal {
  const response = (error as {
    response?: { status?: number; data?: { detail?: unknown } }
  } | null)?.response
  if (!response) {
    return { status: null, code: null, message: 'Could not reach the server. Please retry.' }
  }
  const status = response.status ?? null
  const detail = response.data?.detail
  if (typeof detail === 'string') return { status, code: null, message: detail }
  if (Array.isArray(detail)) {
    const first = detail.find((d) => d && typeof d === 'object' && typeof (d as { msg?: unknown }).msg === 'string')
    return {
      status, code: null,
      message: first ? String((first as { msg: string }).msg) : 'That request was not valid.',
    }
  }
  if (detail && typeof detail === 'object') {
    const body = detail as Record<string, unknown>
    return {
      status,
      code: typeof body.code === 'string' ? body.code : null,
      message: typeof body.message === 'string' ? body.message : 'That decision was refused.',
      decidedBy: typeof body.decided_by === 'string' ? body.decided_by : null,
      decidedAt: typeof body.decided_at === 'string' ? body.decided_at : null,
      withdrawable: typeof body.withdrawable === 'boolean' ? body.withdrawable : undefined,
      overridable: typeof body.overridable === 'boolean' ? body.overridable : undefined,
      record: body.record ? toCamelCase<DataReviewRecordView>(body.record) : null,
    }
  }
  return { status, code: null, message: 'That decision could not be recorded. Please retry.' }
}

/**
 * Everything a decision can have changed, refreshed.
 *
 * It writes the record, the checklist, the dossier coverage, and — on a
 * promoted record — the deal, its lineage and the pipeline's blank-cell
 * markers. A found datapoint also changes the field catalogue.
 */
export async function invalidateAfterDecision(queryClient: QueryClient, loanId?: string | null) {
  const keys = [
    dataReviewKeys.all,
    ['ingestion-queue'],
    ['ingestion-record'],
    ['ingestion-dossier'],
    ['reparse-pending'],
    ['discovered-datapoints'],
    ['field-definitions'],
    ['pipeline-review-gaps'],
  ] as const
  await Promise.all([
    ...keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    loanId ? invalidateLoanQueries(queryClient, loanId) : queryClient.invalidateQueries({ queryKey: ['loans'] }),
  ])
}
